import React from "react";
import { Link, Redirect, useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { UserContext } from "../../contexts/UserContext";
import type { AppConfig } from "../../models/config"
import GoogleLoginButton from 'react-social-login-buttons/lib/buttons/GoogleLoginButton';
import MicrosoftLoginButton from 'react-social-login-buttons/lib/buttons/MicrosoftLoginButton';
import { LoginSocialGoogle } from 'reactjs-social-login';
import { startMicrosoftSignIn, hasPendingMicrosoftSignIn, takeMicrosoftSignIn } from "utils/microsoftSignIn";
import { fixGoogleUserinfoProxy } from "../../utils/fixGoogleUserinfoProxy";
import { buildUser } from "../../utils/user";
import { formatEndDate } from "../../utils/inviteMessage";
import { hasNickname, teamLabel } from "../../utils/team";
import axiosConfig from "../../restApi/axiosConfig";

fixGoogleUserinfoProxy();

const useStyles = makeStyles((theme) => ({
  socialButton: {
    marginBottom: "20px"
  }
}));


type Props = {
  config: AppConfig
}

export const Signup = (props: Props) => {
  const { config } = props;
  const googleAppId = config ? config.socialApps.google : null;
  const microsoftAppId = config ? config.socialApps.microsoft : null;
  const location = useLocation();
  const invitationCode = new URLSearchParams(location.search).get("invitation");
  const [state, setState] = React.useState({
    loading: true,
    team: {},
    error: "",
  });
  const [validationErrors, setValidationErrors] = React.useState(null);
  const [fData, setFData] = React.useState({
    first_name: "",
    last_name: "",
    email: "",
    password1: "",
    password2: "",
  });
  const [login, setLogin] = React.useState(false);
  const [joining, setJoining] = React.useState(false);
  const [joinError, setJoinError] = React.useState("");
  // Google/Microsoft's own SDKs reject silently in a few real-world
  // cases (the SDK script hasn't finished loading yet, a popup blocked
  // by the browser, third-party cookies blocked) - Safari in particular
  // hits these far more often than Chrome. Without this, the only trace
  // was a console.log() nobody but a developer would ever see - from a
  // user's seat, the button just did nothing.
  const [ssoError, setSsoError] = React.useState("");
  const userContext = React.useContext(UserContext);
  const history = useHistory();
  const classes = useStyles();

  React.useEffect(() => {
    if (!invitationCode) {
      setState({ team: {}, loading: false, error: "Broken link" });
      return;
    }
    axios.get("/api/team/invitation/" + invitationCode)
      .then((res) => {
        setState({
          loading: false,
          team: res.data,
          error: "",
        });
      })
      .catch((err) => {
        if (err.response && err.response.status === 401) {
          setState({
            team: {},
            loading: false,
            error: err.response.data,
          });
        } else {
          setState({
            team: {},
            loading: false,
            error: "",
          });
        }
      });
  }, []);

  const socialSignup = (res, provider, accessToken) => {
    if (accessToken) {
      const email = res.email || res.mail || res.userPrincipalName;
      axios
        .post(`/auth/${provider}/signup/`, {
          access_token: accessToken,
          // The server works out the team from the invitation itself - it no
          // longer accepts a team id, which let anyone join any team.
          invitation_code: invitationCode,
        })
        .then((res) => {
          const user = buildUser(res.data.user, true);
          localStorage.setItem("token", res.data.access);
          localStorage.setItem("user", JSON.stringify(user));
          userContext.setUser(user);
          axios.defaults.headers.common[
            "Authorization"
          ] = `Bearer ${localStorage.getItem("token")}`;
          setLogin(true);
        })
        .catch((err) => {
          const code = err && err.response && err.response.data && err.response.data.code;
          if (code === "email_in_use") {
            history.push("/email-in-use", { email });
            return;
          }
          if (code === "verify_to_link") {
            history.push("/verify-to-link", { email });
            return;
          }
          if (code === "not_invited") {
            history.push("/not-invited", { email });
            return;
          }
          if (code === "invalid_invitation") {
            // Expired/invalid link, or (for a new team) not the address the
            // link was sent to - the server's message says which.
            setSsoError(err.response.data.detail);
            return;
          }
          console.log("error");
        });
    }
  }

  const responseGoogle = (res) => {
    if (res && res.access_token) {
      socialSignup(res, 'google', res.access_token);
    }
  };

  const responseMicrosoft = (res) => {
    if (res && res.access_token) {
      socialSignup(res, 'microsoft', res.access_token);
    }
  };

  const handleSsoReject = (providerLabel) => (err) => {
    console.log(err);
    setSsoError(
      `Couldn't sign up with ${providerLabel} - try again, or use email below.`
    );
  };

  // Back from Microsoft's sign-in (utils/microsoftSignIn.js): finish it.
  React.useEffect(() => {
    if (!microsoftAppId || !hasPendingMicrosoftSignIn()) return;
    takeMicrosoftSignIn(microsoftAppId).then(responseMicrosoft).catch(handleSsoReject("Microsoft"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [microsoftAppId]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const data = new FormData(event.target);
    data.append("invitation_code", invitationCode);
    axios
      .post("/auth/registration/", data)
      .then((res) => {
        const user = { ...res.data.user, isAuthenticated: true };
        localStorage.setItem("token", res.data.access);
        localStorage.setItem("user", JSON.stringify(user));
        userContext.setUser(user);
        setLogin(true);
      })
      .catch((err) => {
        if (err.response && err.response.status === 400) {
          setValidationErrors(err.response.data);
        }
      });
  };

  const handleFormChange = (event) => {
    setFData({ ...fData, [event.target.name]: event.target.value });
  };

  // Someone already signed in (with a different team, or several)
  // followed this exact invite link - the same one the Admin page's
  // "Invite a Friend" button already produces for brand-new signups.
  // Joining is just adding a membership to the account they're already
  // using, not creating a new one.
  const handleJoin = () => {
    setJoining(true);
    setJoinError("");
    axiosConfig
      .post("/api/team/join/", { invitation_code: invitationCode })
      .then((res) => {
        const user = buildUser(res.data, true);
        localStorage.setItem("user", JSON.stringify(user));
        userContext.setUser(user);
        setLogin(true);
      })
      .catch((err) => {
        setJoining(false);
        setJoinError(
          (err.response && err.response.data) || "Something went wrong - try again."
        );
      });
  };

  if (login) {
    return <Redirect to="/song-proposals" />;
  }

  return (
    <Container maxWidth="sm">
      <Paper>
        <Box p={2} textAlign="center">
          {state.loading ? (
            ""
          ) : state.error.length > 0 || !state.team.name ? (
            // An invitation that doesn't work says why and what to do, rather
            // than the server's two-word reply.
            <Box py={4}>
              <Typography variant="h6" gutterBottom>
                {state.error.length === 0
                  ? "We couldn't load this invitation"
                  : /expired/i.test(state.error)
                  ? "This invitation has expired"
                  : "This invitation link isn't valid"}
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                {state.error.length === 0
                  ? "Check your connection and try again."
                  : "Ask your team leader to send you a new link."}
              </Typography>
              {state.error.length === 0 ? (
                <Button variant="contained" color="secondary" onClick={() => window.location.reload()}>
                  Try again
                </Button>
              ) : (
                <Typography variant="body2">
                  Already have an account?{" "}
                  <Link to="/login">Sign in</Link>
                </Typography>
              )}
            </Box>
          ) : userContext.user.isAuthenticated ? (
            <Box py={4}>
              <Box mb={4}>
                <Typography variant="h6">{`Join ${teamLabel(state.team)} (${state.team.city})?`}</Typography>
                {hasNickname(state.team) && (
                  <Typography variant="body2" color="textSecondary">
                    {state.team.name}
                  </Typography>
                )}
                <Box mt={1}>
                  <Typography variant="body2" color="textSecondary">
                    {`You're signed in as ${userContext.user.email}. Joining adds this team to your account.`}
                  </Typography>
                </Box>
              </Box>
              {joinError && (
                <Box pb={2}>
                  <Typography color="error" variant="body2">{joinError}</Typography>
                </Box>
              )}
              <Button
                variant="contained"
                color="secondary"
                onClick={handleJoin}
                disabled={joining}
              >
                Join Team
              </Button>
            </Box>
          ) : (
            state.team !== null && (
              <Box py={4}>
                <Box mb={6}>
                  <Typography variant="h6">{`You're invited to join ${teamLabel(state.team)}`}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {/* The official name stays visible: the nickname is whatever a
                        leader typed, and a stranger should still see who this is. */}
                    {hasNickname(state.team) ? `${state.team.name}, ${state.team.city}` : state.team.city}
                  </Typography>
                  <Box mt={2}>
                    <Typography variant="body2">
                      PlanPraise is where your worship team keeps its songs, lyrics and setlists in
                      one place. Create your account below and you'll be added to the team
                      automatically.
                    </Typography>
                  </Box>
                  {state.team.invitation_expires_at && (
                    <Box mt={1}>
                      <Typography variant="caption" color="textSecondary">
                        {`This invitation works until ${formatEndDate(state.team.invitation_expires_at)}.`}
                      </Typography>
                    </Box>
                  )}
                  {state.team.required_email_hint && (
                    <Box mt={1}>
                      <Typography variant="body2" color="textSecondary">
                        {`This link was sent to ${state.team.required_email_hint} - sign up with that email address.`}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Container maxWidth="xs">
                  {ssoError && (
                    <Box pb={2}>
                      <Typography color="error" variant="caption">
                        {ssoError}
                      </Typography>
                    </Box>
                  )}
                  {googleAppId && <LoginSocialGoogle
                      className={classes.socialButton}
                      client_id={googleAppId}
                      scope="openid profile email"
                      discoveryDocs="claims_supported"
                      access_type="offline"
                      onResolve={({ provider, data }) => responseGoogle(data)}
                      onReject={handleSsoReject("Google")}>
                    <GoogleLoginButton text={"SIGN UP WITH GOOGLE"}/>
                  </LoginSocialGoogle>}

                  {microsoftAppId && (
                    <div className={classes.socialButton}>
                      <MicrosoftLoginButton
                        text={"SIGN UP WITH MICROSOFT"}
                        onClick={() =>
                          startMicrosoftSignIn({ clientId: microsoftAppId }).catch(handleSsoReject("Microsoft"))
                        }
                      />
                    </div>
                  )}

                  <hr/>
                  <span>OR</span>
                  <hr/>

                  {validationErrors !== null &&
                    "invitation_code" in validationErrors && (
                      <Box pb={2}>
                        <Typography color="error" variant="caption">
                          {validationErrors.invitation_code}
                        </Typography>
                      </Box>
                    )}
                  {validationErrors !== null &&
                    "non_field_errors" in validationErrors && (
                      <Box pb={2}>
                        <Typography color="error" variant="caption">
                          {validationErrors.non_field_errors}
                        </Typography>
                      </Box>
                    )}
                  <form onSubmit={handleSubmit}>
                    <TextField
                      type="text"
                      label="First name"
                      fullWidth
                      name="first_name"
                      variant="filled"
                      color="secondary"
                      value={fData.first_name}
                      onChange={handleFormChange}
                      helperText={
                        validationErrors !== null &&
                        "first_name" in validationErrors &&
                        validationErrors.first_name
                      }
                      error={
                        validationErrors !== null &&
                        "first_name" in validationErrors
                      }
                    />
                    <Box mt={2}>
                      <TextField
                        type="text"
                        label="Last name"
                        fullWidth
                        name="last_name"
                        variant="filled"
                        color="secondary"
                        value={fData.last_name}
                        onChange={handleFormChange}
                        helperText={
                          validationErrors !== null &&
                          "last_name" in validationErrors &&
                          validationErrors.first_name
                        }
                        error={
                          validationErrors !== null &&
                          "last_name" in validationErrors
                        }
                      />
                    </Box>
                    <Box mt={2}>
                      <TextField
                        type="email"
                        label="Email"
                        fullWidth
                        name="email"
                        variant="filled"
                        color="secondary"
                        value={fData.email}
                        onChange={handleFormChange}
                        helperText={
                          validationErrors !== null &&
                          "email" in validationErrors &&
                          validationErrors.email
                        }
                        error={
                          validationErrors !== null &&
                          "email" in validationErrors
                        }
                      />
                    </Box>
                    <Box mt={2}>
                      <TextField
                        type="password"
                        label="Password"
                        fullWidth
                        name="password1"
                        variant="filled"
                        color="secondary"
                        value={fData.password1}
                        onChange={handleFormChange}
                        helperText={
                          validationErrors !== null &&
                          "password1" in validationErrors &&
                          validationErrors.password1
                        }
                        error={
                          validationErrors !== null &&
                          "password1" in validationErrors
                        }
                      />
                    </Box>
                    <Box mt={2}>
                      <TextField
                        type="password"
                        label="Verify password"
                        fullWidth
                        name="password2"
                        variant="filled"
                        color="secondary"
                        value={fData.password2}
                        onChange={handleFormChange}
                        helperText={
                          validationErrors !== null &&
                          "password2" in validationErrors &&
                          validationErrors.password1
                        }
                        error={
                          validationErrors !== null &&
                          "password2" in validationErrors
                        }
                      />
                    </Box>
                    <Box mt={4} textAlign="right">
                      <Button type="submit" variant="contained" fullWidth>
                        Sign up
                      </Button>
                    </Box>
                  </form>
                  <Box mt={3}>
                    <Typography variant="body2">
                      {"Already have an account? "}
                      <Link to={{ pathname: "/login", state: { from: location } }}>
                        Sign in
                      </Link>
                      {" and you'll come straight back here to join."}
                    </Typography>
                  </Box>
                </Container>
              </Box>
            )
          )}
        </Box>
      </Paper>
    </Container>
  );
};